'use client'

import type { ListSpacesParams } from '@cocoonspace/sdk-js/types/listSpacesParams'
import type { Space } from '@cocoonspace/sdk-js/types/space'
import type { Venue } from '@cocoonspace/sdk-js/types/venue'
import { useCallback, useMemo } from 'react'
import { useListSpaces } from '@cocoonspace/sdk-js/domains/spaces/spaces'
import { getCleanObject } from '@cocoonspace/shared/utils/get-clean-object'
import { Skeleton } from '@cocoonspace/ui/components/skeleton'
import { Plural, useLingui } from '@lingui/react/macro'
import { SearchIcon } from 'lucide-react'
import {
	type inferParserType,
	parseAsArrayOf,
	parseAsString,
	useQueryStates,
} from 'nuqs'

import { SearchCapacityFilter } from '~/domains/search/components/filters/search-capacity-filter'
import { SearchFeaturesFilter } from '~/domains/search/components/filters/search-features-filter'
import { TimeslotFilter } from '~/domains/search/components/filters/timeslot-filter'
import { SpaceCard } from '~/domains/search/components/space-card'

type Filters = inferParserType<typeof parsers>

const parsers = {
	capacity: parseAsArrayOf(parseAsString),
	day: parseAsArrayOf(parseAsString),
	end: parseAsString,
	features: parseAsArrayOf(parseAsString),
	start: parseAsString,
}

const SpaceListWrapper = ({
	venueId,
	filters,
}: {
	venueId: Venue['id']
	filters: Filters
}) => {
	const { t } = useLingui()

	const { data: spacesByFloorGroups, isLoading } = useListSpaces(
		{
			venues: [venueId],
			...(getCleanObject(filters) as unknown as ListSpacesParams),
		},
		{
			query: {
				select: ({ data }) => {
					const groups = data.reduce(
						(acc, space) => {
							const floor = space.address?.floor ?? -1

							acc[floor] = acc[floor] || []
							acc[floor].push(space)

							return acc
						},
						{} as Record<number, Space[]>,
					)

					return Object.entries(groups).map(([floor, spaces]) => ({
						floor: Number(floor) > 0 ? Number(floor) : undefined,
						spaces,
					}))
				},
			},
		},
	)

	const hasMultipleFloors = useMemo(() => {
		const floors = spacesByFloorGroups?.filter(({ floor }) => !!floor)
		return floors?.length && floors.length > 1
	}, [spacesByFloorGroups])

	const getSpaceHref = useCallback(
		(space: Space) => {
			const params = new URLSearchParams()

			if (filters.day) {
				for (const day of filters.day) {
					params.append('day', day)
				}
			}

			if (filters.end) {
				params.set('end', filters.end)
			}

			if (filters.start) {
				params.set('start', filters.start)
			}

			if (filters.day) {
				params.set(
					'step',
					filters.start && filters.end ? 'timeSelect' : 'multiDaysSelect',
				)
			}

			const query = params.toString()

			return `/${space.zone}/${space.slug}${query ? `?${query}` : ''}`
		},
		[filters.day, filters.end, filters.start],
	)

	if (isLoading) {
		return (
			<div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3'>
				{Array.from({ length: 6 }).map((_, i) => (
					<Skeleton
						className='aspect-[366/175]'
						key={i}
					/>
				))}
			</div>
		)
	}

	if (!spacesByFloorGroups?.length) {
		return (
			<div className='flex flex-col items-center justify-center gap-2 py-24 text-center text-slate-400'>
				<SearchIcon className='!size-10' />
				{t`Aucun espace n'est disponible avec les critères de recherche sélectionnés`}
			</div>
		)
	}

	return (
		<div className='flex flex-col gap-y-6'>
			{spacesByFloorGroups.map(({ floor, spaces }) => (
				<div
					className='flex flex-col gap-y-2'
					key={`${spaces[0].venue_slug}-${floor}`}
				>
					{!!hasMultipleFloors && !!floor && (
						<h2 className='text-lg leading-none font-semibold'>
							<Plural
								value={floor}
								one='1er étage'
								other='#ème étage'
							/>
						</h2>
					)}

					<div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3'>
						{spaces.map((space) => (
							<SpaceCard
								key={space.id}
								href={getSpaceHref(space)}
								space={space}
								timeSlot={{
									day: filters.day ?? [],
									end: filters.end ?? '',
									start: filters.start ?? '',
								}}
							/>
						))}
					</div>
				</div>
			))}
		</div>
	)
}

export const VenueSpaces = ({ venue }: { venue: Venue }) => {
	const { t } = useLingui()

	const [filters, setFilters] = useQueryStates(parsers, {
		history: 'push',
		shallow: true,
	})

	return (
		<div className='flex flex-col gap-4'>
			<div className='flex flex-col items-start justify-between gap-2 lg:flex-row lg:items-center lg:gap-4'>
				<h2 className='text-2xl font-semibold'>
					{t`Les Cocoons à cette adresse`}
				</h2>

				<div className='flex flex-wrap gap-2 lg:gap-4'>
					<SearchCapacityFilter
						className='w-[135px]'
						onChange={(capacities) => {
							setFilters({
								capacity: capacities?.length ? capacities : null,
							})
						}}
						value={filters.capacity ?? undefined}
					/>

					<TimeslotFilter
						className='max-md:flex-[3] md:w-[210px]'
						value={{
							day: filters.day,
							end: filters.end,
							start: filters.start,
						}}
						onChange={(timeSlot) => {
							setFilters({
								day: timeSlot?.day || null,
								end: timeSlot?.end || null,
								start: timeSlot?.start || null,
							})
						}}
					/>

					<SearchFeaturesFilter
						onChange={(features) => {
							setFilters({
								features: features || null,
							})
						}}
						value={filters.features as string[] | undefined}
					/>
				</div>
			</div>

			<SpaceListWrapper
				filters={filters}
				venueId={venue.id}
			/>
		</div>
	)
}
